// Footer.js
import React from "react";
import styled, { keyframes, css } from "styled-components";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";

// =======================
// Animations
// =======================
const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.1); }
`;

const shine = keyframes`
  0% { background-position: -100% 0; }
  100% { background-position: 200% 0; }
`;

// =======================
// Styled Components
// =======================
const FooterContainer = styled(motion.footer)`
  position: relative;
  width: 100%;
  background-color: #000000;
  color: #ffffff;
  overflow: hidden;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const BackgroundPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.03;
  background-image:
    radial-gradient(
      circle at 20px 20px,
      rgba(255, 255, 255, 0.1) 1px,
      transparent 1px
    ),
    linear-gradient(45deg, transparent 65%, rgba(245, 158, 11, 0.02) 100%);
  background-size:
    40px 40px,
    100% 100%;
  pointer-events: none;
`;

const MovingGradient = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(245, 158, 11, 0.03),
    rgba(255, 255, 255, 0.02),
    transparent
  );
  pointer-events: none;
`;

const ContentWrapper = styled.div`
  position: relative;
  max-width: 80rem;
  margin: 0 auto;
  padding: 5rem 2rem 3rem;
  z-index: 10;

  @media (min-width: 768px) {
    padding: 6rem 3rem 3rem;
  }

  @media (min-width: 1024px) {
    padding: 8rem 4rem 4rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem 4rem;

  @media (min-width: 768px) {
    grid-template-columns: 1.5fr 1fr;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 2fr 1.2fr;
  }
`;

// Left Column
const LeftColumn = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const BigTitle = styled.h2`
  font-size: clamp(3rem, 10vw, 5rem);
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0.12em;
  margin: 0;
  margin-bottom: 0.7em;
  /* text-transform: uppercase; */
  display: flex;
  font-family: "Ams", sans-serif;
  flex-wrap: wrap;
`;

const Letter = styled(motion.span)`
  display: inline-block;
  cursor: default;

  &:hover {
    color: #f59e0b;
    transform: translateY(-5px);
    transition: all 0.3s ease;
  }
`;

const Tagline = styled(motion.p)`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.6);
  max-width: 28rem;
  line-height: 1.6;
  margin: 0;
`;

const SocialContainer = styled(motion.div)`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: white;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #f59e0b, #d97706);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: #f59e0b;

    &::before {
      opacity: 0.2;
    }

    span {
      color: #f59e0b;
      transform: scale(1.1);
    }
  }
`;

const SocialLabel = styled.span`
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
`;

const Tooltip = styled.span`
  position: absolute;
  bottom: -2rem;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;

  ${SocialLink}:hover & {
    opacity: 1;
  }
`;

const Divider = styled(motion.div)`
  width: 6rem;
  height: 2px;
  background: linear-gradient(90deg, #f59e0b, transparent);
  margin: 1rem 0;
`;

// Right Column
const RightColumn = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const NewsletterSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border-radius: 1rem;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const NewsletterTitle = styled.h3`
  font-size: 0.875rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #f59e0b;
  margin: 0 0 0.5rem;
`;

const NewsletterDesc = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 1.5rem;
`;

const Form = styled.form`
  position: relative;
  width: 100%;
`;

const Input = styled(motion.input)`
  width: 100%;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  padding: 1rem 1.5rem;
  color: white;
  font-size: 0.875rem;
  outline: none;
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    border-color: #f59e0b;
    background: rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.1);
  }
`;

const SubmitButton = styled(motion.button)`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 2.5rem;
  height: 2.5rem;
  background: #f59e0b;
  border: none;
  border-radius: 50%;
  color: black;
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: #d97706;
    width: 3rem;
  }
`;

// Links Grid
const LinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
`;

const LinkColumn = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ColumnTitle = styled.h4`
  font-size: 0.875rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #f59e0b;
  margin: 0;
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const LinkItem = styled(motion.li)`
  margin: 0;
`;

const StyledLink = styled.a`
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  display: inline-block;
  cursor: pointer;

  &:hover {
    color: #f59e0b;
    transform: translateX(5px);
  }
`;

// Bottom Bar
const BottomBar = styled(motion.div)`
  margin-top: 4rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.3);

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Copyright = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Heart = styled(motion.span)`
  display: inline-block;
  color: #f59e0b;
  font-size: 1rem;
`;

const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Badge = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 999px;
  font-size: 0.75rem;
`;

const Dot = styled.span`
  width: 0.25rem;
  height: 0.25rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
`;

// Decorative Elements
const LensDecoration = styled(motion.div)`
  position: absolute;
  bottom: -5rem;
  right: -5rem;
  width: 20rem;
  height: 20rem;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.02);
  opacity: 0.3;
  pointer-events: none;
`;

const LensInner = styled.div`
  position: absolute;
  inset: 2rem;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.02);
`;

const LensCore = styled.div`
  position: absolute;
  inset: 4rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.02);
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  width: 0.5rem;
  height: 0.5rem;
  background: #f59e0b;
  border-radius: 50%;
  filter: blur(2px);
  opacity: 0.2;
`;

// =======================
// Main Footer Component
// =======================
const Footer = () => {
  const footerRef = React.useRef(null);

  const { scrollYProgress } = useScroll({
    target: footerRef,
    offset: ["start end", "end end"],
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    mass: 0.5,
  });

  const y = useTransform(smoothProgress, [0, 1], [100, 0]);
  const opacity = useTransform(smoothProgress, [0, 0.5, 1], [0, 0.5, 1]);
  const scale = useTransform(smoothProgress, [0, 1], [0.95, 1]);

  const bigText = "Bess Hamiti".split("");

  // Floating elements positions
  const floatingPositions = [
    { top: "10%", left: "5%" },
    { top: "30%", right: "10%" },
    { top: "70%", left: "15%" },
    { top: "50%", right: "20%" },
    { top: "85%", left: "25%" },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Add newsletter signup logic here
  };

  return (
    <FooterContainer
      ref={footerRef}
      style={{ opacity, scale }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <BackgroundPattern />

      <MovingGradient
        animate={{
          x: ["-100%", "200%"],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear",
        }}
      />

      {floatingPositions.map((pos, i) => (
        <FloatingElement
          key={i}
          style={pos}
          animate={{
            y: [0, -30, 0],
            opacity: [0.1, 0.3, 0.1],
          }}
          transition={{
            duration: 4 + i,
            repeat: Infinity,
            delay: i * 0.5,
          }}
        />
      ))}

      <ContentWrapper>
        <Grid>
          {/* Left Column - Big Text & Social */}
          <LeftColumn style={{ y }}>
            <BigTitle>
              {bigText.map((letter, i) => (
                <Letter
                  key={i}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.5,
                    delay: i * 0.03,
                  }}
                >
                  {letter === " " ? "\u00A0" : letter}
                </Letter>
              ))}
            </BigTitle>

            <Tagline
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              Preserving nature's beauty through the lens — over 15 years of
              capturing moments that last forever.
            </Tagline>

            <SocialContainer
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
            >
              {[
                { name: "IG", label: "Instagram" },
                { name: "TW", label: "Twitter/X" },
                { name: "PT", label: "Pinterest" },
                { name: "YT", label: "YouTube" },
              ].map((social, i) => (
                <SocialLink
                  key={social.name}
                  href="#"
                  whileHover={{ y: -5, scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.8 + i * 0.1 }}
                >
                  <SocialLabel>{social.name}</SocialLabel>
                  <Tooltip>{social.label}</Tooltip>
                </SocialLink>
              ))}
            </SocialContainer>

            <Divider
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1, duration: 0.8 }}
            />
          </LeftColumn>

          {/* Right Column - Newsletter & Links */}
          <RightColumn
            style={{ y: useTransform(smoothProgress, [0, 1], [50, 0]) }}
          >
            {/* Newsletter */}
            <NewsletterSection>
              <NewsletterTitle>Stay Inspired</NewsletterTitle>
              <NewsletterDesc>
                Get exclusive access to new collections and behind-the-scenes
                stories.
              </NewsletterDesc>

              <Form onSubmit={handleSubmit}>
                <Input
                  type="email"
                  placeholder="Your email address"
                  whileFocus={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                />
                <SubmitButton
                  type="submit"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  →
                </SubmitButton>
              </Form>
            </NewsletterSection>

            {/* Links Grid */}
            <LinksGrid>
              {[
                {
                  title: "Explore",
                  links: ["Portfolio", "Collections", "AI Gallery", "Shop"],
                },
                {
                  title: "Info",
                  links: ["About", "Exhibitions", "Publications", "Contact"],
                },
                {
                  title: "Legal",
                  links: ["Privacy", "Terms", "Licensing", "FAQ"],
                },
              ].map((column, colIndex) => (
                <LinkColumn
                  key={column.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + colIndex * 0.1 }}
                >
                  <ColumnTitle>{column.title}</ColumnTitle>
                  <LinkList>
                    {column.links.map((link, linkIndex) => (
                      <LinkItem
                        key={link}
                        whileHover={{ x: 5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <StyledLink href="#">{link}</StyledLink>
                      </LinkItem>
                    ))}
                  </LinkList>
                </LinkColumn>
              ))}
            </LinksGrid>
          </RightColumn>
        </Grid>

        {/* Bottom Bar */}
        <BottomBar
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1.2 }}
        >
          <Copyright>© 2024 Lens & Canvas. All rights reserved.</Copyright>

          <BadgeContainer>
            <Badge>15+ Years of Excellence</Badge>
            <Dot />
            <Badge>
              Made with{" "}
              <Heart
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                ♥
              </Heart>{" "}
              for nature
            </Badge>
          </BadgeContainer>
        </BottomBar>
      </ContentWrapper>

      {/* Decorative Lens */}
      <LensDecoration
        animate={{
          rotate: 360,
          scale: [1, 1.1, 1],
        }}
        transition={{
          rotate: {
            duration: 30,
            repeat: Infinity,
            ease: "linear",
          },
          scale: {
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
          },
        }}
      >
        <LensInner />
        <LensCore />
      </LensDecoration>
    </FooterContainer>
  );
};

export default Footer;
